import {
  JudgeReviewAnnotation,
  ScribbleOptions,
  SignatureType,
} from '@data-contracts';
import {
  AnnotationType,
  CommentOptions,
  JudgeAnnotation,
} from '@feature/jr/shared/models/annotation.model';

/**
 * Rehydrates a persisted `commentOptions` blob into the FE {@link CommentOptions}.
 *
 * The DTO's `ScribbleOptions` declares every flag optional, while the FE model
 * declares each one a required `boolean`. Each flag is therefore defaulted
 * explicitly rather than asserted across with a cast: a cast would let the type
 * checker believe `includeCourt` is a `boolean` when the API had in fact omitted
 * it, and the first strict-equality check written against one of these flags
 * would then be wrong with no compiler help. Consumers use truthy checks today,
 * so this is about keeping the type honest, not fixing a live bug.
 *
 * @param options The persisted option flags, or `undefined` on a non-comment annotation.
 * @returns The fully-populated FE options, or `undefined` when none were persisted.
 */
function mapCommentOptionsFromDto(
  options: ScribbleOptions | null | undefined,
): CommentOptions | undefined {
  if (!options) {
    return undefined;
  }
  return {
    includeCourt: !!options.includeCourt,
    includeCaseNumber: !!options.includeCaseNumber,
    includeFilingId: !!options.includeFilingId,
    includeJudgeName: !!options.includeJudgeName,
    includeAlternateJudgeName: !!options.includeAlternateJudgeName,
    includeCurrentDate: !!options.includeCurrentDate,
    includeCaseName: !!options.includeCaseName,
    includeJudgeSignature: !!options.includeJudgeSignature,
  };
}

/**
 * Maps the persisted annotation DTOs of a document to the FE
 * {@link JudgeAnnotation}[] shape, including the `commentSignatureType`
 * conversion from the DTO's {@link SignatureType} string enum to the FE's numeric
 * `0 | 1`.
 *
 * @param annotations The persisted annotations (may be absent).
 * @returns The annotation objects, or an empty array when none exist.
 */
export function mapPersistedAnnotations(
  annotations: JudgeReviewAnnotation[] | null | undefined,
): JudgeAnnotation[] {
  return (annotations ?? []).map(a => ({
    id: a.id ?? crypto.randomUUID(),
    type: (a.type ?? 'text') as AnnotationType,
    pageNumber: a.pageNumber ?? 1,
    xPct: a.xPct ?? 0,
    yPct: a.yPct ?? 0,
    text: a.text ?? undefined,
    imageDataUrl: a.imageDataUrl ?? undefined,
    commentOptions: mapCommentOptionsFromDto(a.commentOptions),
    commentSignatureType:
      a.commentSignatureType === SignatureType.Graphic
        ? 1
        : a.commentSignatureType === SignatureType.Slash
          ? 0
          : undefined,
    commentText: a.commentText ?? undefined,
    widthPct: a.widthPct ?? undefined,
  }));
}

/**
 * Maps the FE {@link JudgeAnnotation}[] to the API {@link JudgeReviewAnnotation}[]
 * shape, including the `commentSignatureType` conversion between the FE's numeric
 * `0 | 1` and the DTO's {@link SignatureType} string enum.
 *
 * Keeping the conversion here avoids importing `@data-contracts` in the studio
 * component.
 *
 * @param annotations The current in-memory annotation set to convert.
 * @returns The API-ready annotation array.
 */
export function mapAnnotationsToDto(
  annotations: JudgeAnnotation[],
): JudgeReviewAnnotation[] {
  return annotations.map(a => ({
    id: a.id,
    type: a.type,
    pageNumber: a.pageNumber,
    xPct: a.xPct,
    yPct: a.yPct,
    text: a.text ?? null,
    imageDataUrl: a.imageDataUrl ?? null,
    commentOptions: a.commentOptions ?? null,
    commentSignatureType:
      a.commentSignatureType != null
        ? a.commentSignatureType === 1
          ? SignatureType.Graphic
          : SignatureType.Slash
        : null,
    commentText: a.commentText ?? null,
    widthPct: a.widthPct ?? null,
  }));
}
